/**
 * Decodes hash path segments from parent/shell payloads (may be multiply encoded).
 * Used by LOCATION_HASH_CHANGE handling and SMM OAuth redirect stub detection.
 */
export function decodeHashPathForRouting(rawPath: string): string {
  let decodedPath = rawPath;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const nextDecodedPath = decodeURIComponent(decodedPath);
      if (nextDecodedPath === decodedPath) break;
      decodedPath = nextDecodedPath;
    } catch {
      break;
    }
  }
  return decodedPath;
}

/** Decoded pathname placeholders NGP uses after Facebook/Instagram OAuth (post–GUTS-6869 decode). */
const DECODED_OAUTH_STUB_PATHS = new Set(['/_', '/_=_', '/*=*']);

/** Facebook appends `#_=_` on the parent URL; HashRouter may omit the leading `/`. */
const DECODED_OAUTH_STUB_PATHS_NO_LEADING_SLASH = new Set(['_=_']);

/** Encoded placeholders as sent in locationHash before in-app decode. */
const ENCODED_OAUTH_STUB_PATHS = new Set(['/_%3D_', '/*%3D*']);

/**
 * Normalizes hash pathnames so OAuth stubs from Facebook (`#_=_`) match routing handlers.
 */
export function normalizeHashPathnameForRouting(pathname: string): string {
  if (!pathname) {
    return pathname;
  }
  const decoded = decodeHashPathForRouting(pathname);
  if (DECODED_OAUTH_STUB_PATHS_NO_LEADING_SLASH.has(decoded)) {
    return `/${decoded}`;
  }
  return decoded;
}

/**
 * Builds the in-app route after OAuth when `redirectUrl` and SMM params sit as sibling query keys
 * (shell sends `?redirectUrl=/social-media-monitoring/add-account-success&network=Facebook&…`).
 */
export function buildSmmOAuthStubNavigateTarget(search: string): string | null {
  const query = search.startsWith('?') || !search ? search : `?${search}`;
  const params = new URLSearchParams(query);
  const redirectUrl = params.get('redirectUrl') || params.get('redirecturl');
  if (!redirectUrl) {
    return null;
  }

  const path = decodeURIComponent(redirectUrl);
  const extraParams = new URLSearchParams();
  params.forEach((value, key) => {
    if (key !== 'redirectUrl' && key !== 'redirecturl') {
      extraParams.set(key, value);
    }
  });
  const extraQuery = extraParams.toString();
  if (!extraQuery) {
    return path;
  }
  return path.includes('?') ? `${path}&${extraQuery}` : `${path}?${extraQuery}`;
}

/**
 * True when the pathname is the temporary OAuth callback stub (encoded or decoded).
 * @see App.tsx handleRedirectIssue
 */
export function isSocialMediaOAuthRedirectStubPathname(pathname: string | undefined | null): boolean {
  if (!pathname) {
    return false;
  }
  const normalized = normalizeHashPathnameForRouting(pathname);
  if (ENCODED_OAUTH_STUB_PATHS.has(pathname) || DECODED_OAUTH_STUB_PATHS.has(normalized)) {
    return true;
  }
  // Multiply-encoded or variant shell payloads (e.g. /_%253D_) that decode to /_=_ or /*=*.
  return DECODED_OAUTH_STUB_PATHS.has(decodeHashPathForRouting(normalized));
}
